import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { selectToken } from "../../app/slices/AuthenticationSlice";
import { setIsLoading } from "../../app/slices/GlobalSlice";
import {
  LanguageData,
  addAutoTranslatedLanguage,
  addMissingAutoTranslatedKeys,
  selectTrainingLocalizationState,
} from "../../app/slices/TrainingLocalizationSlice";
import {
  selectReferenceLanguage,
} from "../../app/slices/TrainingSlice";
import { TranslationService } from "../../services/TranslationService";
import { v4 as uuidv4 } from "uuid";
import {
  beginHistoryTransaction,
  cancelHistoryTransaction,
  commitHistoryTransaction,
} from "../../app/slices/HistorySlice";
import {
  TRANSLATION_FAILED_KEY,
  TRANSLATION_FAILURE_TOAST_MS,
} from "../../helpers/translationFeedback";

export default function useAutotranslation() {
  const locState = useSelector(selectTrainingLocalizationState);
  const token = useSelector(selectToken);
  const referenceLanguage = useSelector(selectReferenceLanguage);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  /**
   * Reported here rather than rethrown: every caller invokes these from a click
   * handler without awaiting, so rethrowing would only produce an unhandled
   * rejection and the editor would still be told nothing. The transaction has
   * already been cancelled by the time this runs, so no partial write survives.
   */
  const reportFailure = () =>
    toast.error(t(TRANSLATION_FAILED_KEY) as string, {
      position: "top-right",
      autoClose: TRANSLATION_FAILURE_TOAST_MS,
    });

  const translateAll = async (lang: LanguageData) => {
    var from = referenceLanguage;
    var to = lang.shortcode;
    var texts: string[] = [];

    for (var key in locState.localizedEntries) {
      texts.push(locState.localizedEntries[key][from].value);
    }

    var translatedTexts = await TranslationService.TranslateTexts(
      token,
      texts,
      from,
      to
    );

    dispatch(
      addAutoTranslatedLanguage({
        lang: lang,
        translatedValues: translatedTexts,
      })
    );

  };

  const translateMissing = async (lang: LanguageData) => {
    var from = referenceLanguage;
    var to = lang.shortcode;
    var texts: string[] = [];

    for (var key in locState.localizedEntries) {
      texts.push(locState.localizedEntries[key][from].value);
    }

    //dispatch(setIsLoading(true));
    var translatedTexts = await TranslationService.TranslateTexts(
      token,
      texts,
      from,
      to
    );

    dispatch(
      addMissingAutoTranslatedKeys({
        lang: lang,
        translatedValues: translatedTexts,
      })
    );

    // dispatch(setIsLoading(false));
  };

  const translateMultipleMissing = async (langData: LanguageData[]) => {
    const transaction = { id: `translate-missing-${uuidv4()}` };
    dispatch(setIsLoading(true));
    dispatch(beginHistoryTransaction(transaction));
    try {
      for (var lang of langData) {
        if (lang.shortcode === referenceLanguage) continue;
        await translateMissing(lang);
      }
      dispatch(commitHistoryTransaction(transaction));
    } catch (error) {
      dispatch(cancelHistoryTransaction(transaction));
      reportFailure();
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const translateMultipleAll = async (langData: LanguageData[]) => {
    const transaction = { id: `translate-all-${uuidv4()}` };
    dispatch(setIsLoading(true));
    dispatch(beginHistoryTransaction(transaction));
    try {
      for (var lang of langData) {
        if (lang.shortcode === referenceLanguage) continue;
        await translateAll(lang);
      }
      dispatch(commitHistoryTransaction(transaction));
    } catch (error) {
      dispatch(cancelHistoryTransaction(transaction));
      reportFailure();
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  return {
    translateMultipleMissing,
    translateMultipleAll,
  } as const;
}
